import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar, HelpCircle, Package, X, Zap } from 'lucide-react';
import paths from 'routes/paths';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import usePanelPermissions from 'hooks/usePanelPermissions';
import { PERMISSIONS } from 'helpers/panelPermissions';

const ACTIONS = [
    { label: 'Criar Evento', icon: Calendar, path: `${paths.dashboard.events}?create=1`, permissions: [PERMISSIONS.EVENTS_CREATE] },
    { label: 'Criar Produto', icon: Package, path: `${paths.dashboard.products}?create=1`, permissions: [PERMISSIONS.PRODUCTS_MANAGE] },
    { label: 'Ajuda', icon: HelpCircle, path: paths.dashboard.support },
];

export default function QuickActionsButton() {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { can } = usePanelPermissions();
    const actions = ACTIONS.filter((action) => !action.permissions || can(...action.permissions));

    const inEstablishmentPanel = location.pathname.startsWith(paths.dashboard.establishment.root);

    useEffect(() => {
        if (!open) return;

        function handleClickOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    if (inEstablishmentPanel) return null;

    function handleActionClick(path) {
        setOpen(false);
        navigate(path);
    }

    return (
        <Container ref={containerRef}>
            {open && (
                <ActionsList>
                    {actions.map((action, index) => (
                        <ActionItem
                            key={action.label}
                            type='button'
                            $delay={index * 30}
                            onClick={() => handleActionClick(action.path)}
                        >
                            <ActionIcon>
                                <action.icon size={16} />
                            </ActionIcon>
                            {action.label}
                        </ActionItem>
                    ))}
                </ActionsList>
            )}

            <FabButton
                type='button'
                $open={open}
                onClick={() => setOpen(prev => !prev)}
                title='Ações rápidas'
            >
                {open ? <X size={22} /> : <Zap size={22} />}
            </FabButton>
        </Container>
    );
}

const fabItemIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 29;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;

    @media screen and (min-width: ${breakpoints.lg}) {
        right: 32px;
        bottom: 32px;
    }
`;

const ActionsList = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
`;

const ActionItem = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px 10px 10px;
    border: none;
    border-radius: 30px;
    background: ${colors.nightDark};
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
    animation: ${fabItemIn} 0.18s ease both;
    animation-delay: ${({ $delay }) => $delay}ms;
    transition: background 0.15s ease;

    &:hover {
        background: ${panelAccent(colors.occasionalPurple)};
    }
`;

const ActionIcon = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    flex-shrink: 0;
`;

const FabButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    background: ${panelAccent(colors.occasionalPurple)};
    color: #fff;
    box-shadow: 0 6px 20px ${panelAccentAlpha(colors.occasionalPurple, '55')};
    transition: transform 0.2s ease, background 0.15s ease;
    flex-shrink: 0;

    &:hover {
        background: ${panelAccent(colors.secondaryPurple)};
    }

    ${({ $open }) => $open && `
        transform: rotate(90deg);
    `}
`;
